import { HEROES } from '../heroes';
import { pushLedger } from './ledger';
import type { Profile } from './types';
import { POWERUPS, offersFor, powerIsFree, powerupById, type PowerupId, type TrickTier } from '../powerups';
import type { HeroId } from '../types';

/** Copies a road stop grants, and the stock an old owned trick becomes. */
export const ROAD_TRICK_GRANT = 5;
export const STOCK_CAP = 999;

export const FREE_TRICKS = ['pinecone-mallet', 'leaf-gust'] as const;

export function isFreeTrick(id: string): boolean {
  return (FREE_TRICKS as readonly string[]).includes(id) || powerupById(id)?.free === true;
}

export function trickStockOf(profile: Profile, id: string): number {
  if (isFreeTrick(id)) return Number.POSITIVE_INFINITY;
  return profile.trickStock?.[id] ?? 0;
}

export function addTrickStock(profile: Profile, id: string, count: number): void {
  if (isFreeTrick(id) || count <= 0) return;
  if (!profile.trickStock) profile.trickStock = {};
  const next = Math.min(STOCK_CAP, (profile.trickStock[id] ?? 0) + count);
  profile.trickStock[id] = next;
}

export function trickUnlocked(profile: Profile, id: string): boolean {
  if (isFreeTrick(id)) return true;
  return !!profile.tricks[id];
}

/** Class tricks for that class, Grove for Fern, Posy, and Dash, Universal for everyone. */
export function trickFitsHero(heroId: HeroId, id: string): boolean {
  if (isFreeTrick(id)) return true;
  return offersFor(heroId).some((power) => power.id === id);
}

export interface PackPrice {
  acorns: number | null;
  honey: number | null;
}

const PACKS: Record<TrickTier, { three: PackPrice; ten: PackPrice }> = {
  sprout: { three: { acorns: 120, honey: null }, ten: { acorns: 350, honey: 35 } },
  bramble: { three: { acorns: 240, honey: null }, ten: { acorns: 700, honey: 70 } },
  oak: { three: { acorns: 360, honey: 40 }, ten: { acorns: null, honey: 120 } },
  moonlit: { three: { acorns: null, honey: 60 }, ten: { acorns: null, honey: 180 } },
};

export function packPrice(id: string, size: 3 | 10): PackPrice | null {
  const def = powerupById(id);
  if (!def || def.free) return null;
  return size === 3 ? PACKS[def.tier].three : PACKS[def.tier].ten;
}

export function buyTrickPack(
  profile: Profile,
  id: string,
  size: 3 | 10,
  currency: 'acorns' | 'honey',
  now: number,
): { ok: boolean; reason?: 'missing' | 'locked' | 'acorns' | 'honey' | 'free' } {
  const def = powerupById(id);
  if (!def) return { ok: false, reason: 'missing' };
  if (def.free || isFreeTrick(id)) return { ok: false, reason: 'free' };
  if (!trickUnlocked(profile, id)) return { ok: false, reason: 'locked' };
  const price = packPrice(id, size);
  const cost = price ? price[currency] : null;
  if (cost === null) return { ok: false, reason: currency };
  if (profile.wallet[currency] < cost) return { ok: false, reason: currency };
  profile.wallet[currency] -= cost;
  addTrickStock(profile, id, size);
  pushLedger(profile, now, 'trick', { [currency]: -cost }, 0, `${id}:${size}`);
  return { ok: true };
}

/**
 * A used Trick leaves the bag. An unused one stays.
 * Free Tricks are never spent. Missing stock is ignored.
 */
export function spendUsedTricks(profile: Profile, ids: readonly string[], now: number): string[] {
  const spent: string[] = [];
  for (const id of ids) {
    if (isFreeTrick(id)) continue;
    const have = profile.trickStock?.[id] ?? 0;
    if (have <= 0) continue;
    profile.trickStock[id] = have - 1;
    spent.push(id);
    pushLedger(profile, now, 'trick', {}, 0, id);
  }
  return spent;
}

/** First empty slot, otherwise slot 2. A trick with no stock falls back to Mallet, then Gust. */
export function fallbackLoadout(heroId: HeroId, ids: readonly string[], profile: Profile): PowerupId[] {
  const next: string[] = [];
  for (const id of ids) {
    if (!trickFitsHero(heroId, id)) continue;
    if (!trickUnlocked(profile, id)) continue;
    if (trickStockOf(profile, id) <= 0) continue;
    if (next.includes(id)) continue;
    next.push(id);
    if (next.length === 2) break;
  }
  for (const free of FREE_TRICKS) {
    if (next.length >= 2) break;
    if (!next.includes(free)) next.push(free);
  }
  return next as PowerupId[];
}

export function classLabel(heroId: HeroId): string {
  const hero = HEROES[heroId];
  const name = hero.classId === 'grove' ? 'Grove' : hero.classId[0].toUpperCase() + hero.classId.slice(1);
  return `${hero.name} · ${name}`;
}

export function tricksForFilter(heroId: HeroId, filter: 'mine' | 'all'): { def: (typeof POWERUPS)[number]; equippable: boolean }[] {
  const mine = new Set(offersFor(heroId).map((power) => power.id));
  const list = filter === 'mine' ? POWERUPS.filter((power) => mine.has(power.id)) : [...POWERUPS];
  return list
    .filter((power) => !powerIsFree(power))
    .map((def) => ({ def, equippable: mine.has(def.id) }));
}
